const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  async request(path, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      // Handle 401 - auto logout
      if (response.status === 401 && path !== '/api/auth/login' && path !== '/api/auth/signup') {
        this.setToken(null);
        window.location.href = '/login';
      }
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Auth
  signup(email, password) {
    return this.request('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  login(email, password) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  getMe() {
    return this.request('/api/auth/me');
  }

  // Scan (public)
  scan(url) {
    return this.request('/api/scan', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  // Businesses
  getBusinesses() {
    return this.request('/api/businesses');
  }

  createBusiness(data) {
    return this.request('/api/businesses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  deleteBusiness(businessId) {
    return this.request(`/api/businesses/${businessId}`, {
      method: 'DELETE',
    });
  }

  onboardBusiness(data) {
    return this.request('/api/businesses/onboard', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  onboardStatus(jobId) {
    return this.request(`/api/businesses/onboard/status/${jobId}`);
  }

  analyzeWebsite(url) {
    return this.request('/api/businesses/analyze', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  analyzeStatus(jobId) {
    return this.request(`/api/businesses/analyze/status/${jobId}`);
  }

  refreshLeads(businessId) {
    return this.request(`/api/businesses/${businessId}/refresh`, {
      method: 'POST',
    });
  }

  // Keywords
  getKeywords(businessId) {
    return this.request(`/api/keywords/${businessId}`);
  }

  addKeyword(businessId, keyword) {
    return this.request('/api/keywords', {
      method: 'POST',
      body: JSON.stringify({ business_id: businessId, keyword }),
    });
  }

  toggleKeyword(keywordId) {
    return this.request(`/api/keywords/${keywordId}/toggle`, {
      method: 'PATCH',
    });
  }

  deleteKeyword(keywordId) {
    return this.request(`/api/keywords/${keywordId}`, {
      method: 'DELETE',
    });
  }

  // Subreddits
  getSubreddits(businessId) {
    return this.request(`/api/subreddits/${businessId}`);
  }

  addSubreddit(businessId, name) {
    return this.request('/api/subreddits', {
      method: 'POST',
      body: JSON.stringify({ business_id: businessId, name }),
    });
  }

  toggleSubreddit(subredditId) {
    return this.request(`/api/subreddits/${subredditId}/toggle`, {
      method: 'PATCH',
    });
  }

  deleteSubreddit(subredditId) {
    return this.request(`/api/subreddits/${subredditId}`, {
      method: 'DELETE',
    });
  }

  // Leads
  getLeads(businessId, params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        query.append(key, val);
      }
    });
    return this.request(`/api/leads/${businessId}?${query.toString()}`);
  }

  getLead(leadId) {
    return this.request(`/api/lead/${leadId}`);
  }

  updateLeadStatus(leadId, status) {
    return this.request(`/api/lead/${leadId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  // AI Reply
  generateReply(leadId, tone = 'helpful') {
    return this.request(`/api/lead/${leadId}/reply`, {
      method: 'POST',
      body: JSON.stringify({ tone }),
    });
  }

  // Admin / Ops
  getAdminUsers() {
    return this.request('/api/admin/users');
  }

  getAdminUser(userId) {
    return this.request(`/api/admin/users/${userId}`);
  }

  updateUserPlan(userId, data) {
    return this.request(`/api/admin/users/${userId}/plan`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  suspendUser(userId) {
    return this.request(`/api/admin/users/${userId}/suspend`, {
      method: 'PATCH',
    });
  }

  resetUserPassword(userId, newPassword) {
    return this.request(`/api/admin/users/${userId}/reset-password`, {
      method: 'PATCH',
      body: JSON.stringify({ new_password: newPassword }),
    });
  }

  resetUserAiReplies(userId) {
    return this.request(`/api/admin/users/${userId}/reset-ai-replies`, {
      method: 'PATCH',
    });
  }

  addAdminNote(userId, note) {
    return this.request(`/api/admin/users/${userId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  deleteUser(userId) {
    return this.request(`/api/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  getAdminStats() {
    return this.request('/api/admin/stats');
  }

  getUserBilling(userId) {
    return this.request(`/api/admin/users/${userId}/billing`);
  }

  userBillingAction(userId, data) {
    return this.request(`/api/admin/users/${userId}/billing/action`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Plan
  getPlanInfo() {
    return this.request('/api/plan');
  }

  // Announcement
  getAnnouncement() {
    return this.request('/api/announcement');
  }

  updateAnnouncement(data) {
    return this.request('/api/admin/announcement', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Stripe
  createCheckout(packageId, couponCode = null) {
    const body = { package_id: packageId, origin_url: window.location.origin };
    if (couponCode) body.coupon_code = couponCode;
    return this.request('/api/stripe/checkout', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Coupons
  getAdminCoupons() {
    return this.request('/api/admin/coupons');
  }

  createCoupon(data) {
    return this.request('/api/admin/coupons', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateCoupon(couponId, data) {
    return this.request(`/api/admin/coupons/${couponId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  deleteCoupon(couponId) {
    return this.request(`/api/admin/coupons/${couponId}`, {
      method: 'DELETE',
    });
  }

  validateCoupon(code, packageId) {
    return this.request('/api/coupons/validate', {
      method: 'POST',
      body: JSON.stringify({ code, package_id: packageId }),
    });
  }

  // Notifications & Scanning
  getNotificationSettings(businessId) {
    return this.request(`/api/businesses/${businessId}/notifications`);
  }

  updateNotificationSettings(businessId, settings) {
    return this.request(`/api/businesses/${businessId}/notifications`, {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  testSlackNotification(businessId) {
    return this.request(`/api/businesses/${businessId}/test-slack`, {
      method: 'POST',
    });
  }

  getScanLogs(businessId, limit = 20) {
    return this.request(`/api/businesses/${businessId}/scan-logs?limit=${limit}`);
  }
}

export const api = new ApiClient();
export default api;
